import React, { useState, useMemo, useCallback } from "react";
import { CheckCircle2, Circle, ChevronDown, ChevronRight, AlertTriangle, Calendar, FileText, PenTool, Info, Bot } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * ChecklistEngine — Shared reusable component for Pre-Exchange and Pre-Completion checklists.
 * Accepts a config object defining sections/items and renders the dynamic checklist.
 *
 * Props:
 *  - config: The checklist config (preExchangeConfig or preCompletionConfig)
 *  - items: Current saved items state { [field_id]: { checked, value, date, notes, amount } }
 *  - onItemChange: (fieldId, fieldData) => void
 *  - transactionType: "sale" | "purchase" | "linked"
 *  - isLeasehold: boolean
 *  - isMortgaged: boolean
 *  - isCompany: boolean (BM-H4191; only the refinance checklist passes it)
 *  - readOnly: boolean (for review mode)
 *  - onSign: (signature) => void (callback for sign-off)
 *  - signature: string | null (existing signature)
 *  - matterReference / clientName: BM-H4384. When set, a section's agentSkills
 *    show as buttons that open the conveyancing agent on this matter with that
 *    task picked (Bonsai Agents page).
 */
// BM-H4384: the Bonsai Agents page opens a new conveyancing review from these
// query parameters (pages/BonsaiAgents.jsx reads new, agent, matter, client, task).
export function agentSkillHref(matterReference, clientName, task) {
  const q = new URLSearchParams({ new: "1", agent: "conveyancing", matter: matterReference, client: clientName || "", task });
  return `/bonsai-agents?${q.toString()}`;
}

export default function ChecklistEngine({
  config,
  items = {},
  onItemChange,
  transactionType = "purchase",
  isLeasehold = false,
  isMortgaged = false,
  isCompany = false,
  readOnly = false,
  onSign,
  signature,
  matterReference = "",
  clientName = "",
}) {
  const [collapsedSections, setCollapsedSections] = useState({});
  const [signInput, setSignInput] = useState("");

  // Determine if an item is visible based on transaction type + conditions
  const isItemVisible = useCallback((item) => {
    // Check transaction type
    if (item.applies && !item.applies.includes(transactionType)) return false;
    // Check conditions (is_leasehold, is_mortgaged)
    if (item.condition === "is_leasehold" && !isLeasehold) return false;
    if (item.condition === "is_mortgaged" && !isMortgaged) return false;
    // BM-H4191: company items show for a company client. Pages that do not
    // pass isCompany keep the old behaviour (company items hidden).
    if (item.condition === "is_company" && !isCompany) return false;
    if (item.condition === "not_company" && isCompany) return false;
    // Check conditional visibility (radio-dependent fields)
    if (item.conditionalOn) {
      const parentValue = items[item.conditionalOn]?.value;
      return parentValue === item.conditionalValue;
    }
    return true;
  }, [transactionType, isLeasehold, isMortgaged, isCompany, items]);

  // Determine if a section is visible
  const isSectionVisible = useCallback((section) => {
    if (section.condition === "is_leasehold" && !isLeasehold) return false;
    if (section.condition === "is_company" && !isCompany) return false;
    // A section is visible if at least one item is visible
    return section.items.some(item => isItemVisible(item));
  }, [isLeasehold, isCompany, isItemVisible]);

  // Calculate progress
  const progress = useMemo(() => {
    let total = 0;
    let checked = 0;
    config.sections.forEach(section => {
      if (!isSectionVisible(section)) return;
      section.items.forEach(item => {
        if (!isItemVisible(item)) return;
        if (item.type === "signature") return; // Don't count signature in progress
        if (item.conditionalOn) return; // Conditional fields don't count towards total
        total++;
        const val = items[item.id];
        if (val?.checked || val?.value === "yes" || val?.value === "no") checked++;
      });
    });
    return { total, checked, pct: total > 0 ? Math.round((checked / total) * 100) : 0 };
  }, [config, items, isItemVisible, isSectionVisible]);

  // Per-section progress
  const sectionProgress = useCallback((section) => {
    let total = 0;
    let checked = 0;
    section.items.forEach(item => {
      if (!isItemVisible(item)) return;
      if (item.type === "signature" || item.conditionalOn) return;
      total++;
      const val = items[item.id];
      if (val?.checked || val?.value === "yes" || val?.value === "no") checked++;
    });
    return { total, checked };
  }, [items, isItemVisible]);

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleCheck = (itemId) => {
    if (readOnly) return;
    const current = items[itemId] || {};
    onItemChange(itemId, { ...current, checked: !current.checked });
  };

  const handleFieldChange = (itemId, field, value) => {
    if (readOnly) return;
    const current = items[itemId] || {};
    onItemChange(itemId, { ...current, [field]: value });
  };

  const handleRadioChange = (itemId, value) => {
    if (readOnly) return;
    onItemChange(itemId, { value });
  };

  // Render an individual checklist item
  const renderItem = (item) => {
    if (!isItemVisible(item)) return null;
    const val = items[item.id] || {};

    // Signature type
    if (item.type === "signature") {
      return (
        <div key={item.id} className="p-4 border border-navy-border rounded-md bg-navy-card" data-testid={`checklist-item-${item.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <PenTool className="w-4 h-4 text-teal" />
            <span className="text-sm font-medium text-white">{item.label}</span>
          </div>
          {signature ? (
            <div className="flex items-center gap-2 text-teal">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-sm font-medium">Signed: {signature}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={signInput}
                onChange={e => setSignInput(e.target.value)}
                placeholder="Type your full name to sign"
                className="flex-1 px-3 py-2 text-sm border border-navy-border rounded-md bg-[rgba(26,37,71,0.85)] text-white placeholder:text-txt-muted focus:outline-none focus:ring-2 focus:ring-teal"
                disabled={readOnly}
              />
              <Button
                size="sm"
                onClick={() => onSign?.(signInput)}
                disabled={!signInput.trim() || readOnly}
                className="bg-teal hover:bg-teal-hover text-white"
              >
                Sign
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Radio type (Yes/No)
    if (item.type === "radio") {
      return (
        <div key={item.id} className="flex items-start gap-3 py-3 px-3 rounded-md hover:bg-navy-hover/30 transition-colors" data-testid={`checklist-item-${item.id}`}>
          <div className="flex-1 min-w-0">
            <span className="text-sm text-txt">{item.label}</span>
            {item.required && <span className="text-status-red text-xs ml-1">*</span>}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => handleRadioChange(item.id, "yes")}
              className={`px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${
                val.value === "yes" 
                  ? "bg-teal/20 border-teal/50 text-teal" 
                  : "border-navy-border text-txt-muted hover:border-teal/30"
              }`}
              disabled={readOnly}
            >
              Yes
            </button>
            <button
              onClick={() => handleRadioChange(item.id, "no")}
              className={`px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${
                val.value === "no" 
                  ? "bg-status-red/20 border-red-500/50 text-status-red" 
                  : "border-navy-border text-txt-muted hover:border-red-500/30"
              }`}
              disabled={readOnly}
            >
              No
            </button>
          </div>
        </div>
      );
    }

    // Date type (standalone)
    if (item.type === "date") {
      return (
        <div key={item.id} className="flex items-center gap-3 py-3 px-3 rounded-md hover:bg-navy-hover/30 transition-colors" data-testid={`checklist-item-${item.id}`}>
          <Calendar className="w-4 h-4 text-txt-muted flex-shrink-0" />
          <span className="text-sm text-txt flex-1">{item.label}</span>
          <input
            type="date"
            value={val.date || ""}
            onChange={e => handleFieldChange(item.id, "date", e.target.value)}
            className="px-2 py-1 text-xs border border-navy-border rounded bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-1 focus:ring-teal"
            disabled={readOnly}
          />
        </div>
      );
    }

    // Multiline type
    if (item.type === "multiline") {
      return (
        <div key={item.id} className="py-3 px-3 rounded-md hover:bg-navy-hover/30 transition-colors" data-testid={`checklist-item-${item.id}`}>
          <span className="text-sm text-txt block mb-2">{item.label}</span>
          <textarea
            value={val.value || ""}
            onChange={e => handleFieldChange(item.id, "value", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-navy-border rounded-md bg-[rgba(26,37,71,0.85)] text-white placeholder:text-txt-muted focus:outline-none focus:ring-1 focus:ring-teal resize-none"
            placeholder="Enter details..."
            disabled={readOnly}
          />
        </div>
      );
    }

    // Checkbox types (checkbox, checkbox_date, checkbox_amount, checkbox_notes)
    return (
      <div key={item.id} className="flex items-start gap-3 py-3 px-3 rounded-md hover:bg-navy-hover/30 transition-colors" data-testid={`checklist-item-${item.id}`}>
        <button
          onClick={() => handleCheck(item.id)}
          className="mt-0.5 flex-shrink-0"
          disabled={readOnly || !!item.autoCheck}
        >
          {val.checked ? (
            <CheckCircle2 className="w-5 h-5 text-teal" />
          ) : (
            <Circle className="w-5 h-5 text-txt-faint" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${val.checked ? "text-txt-muted line-through" : "text-txt"}`}>
              {item.label}
            </span>
            {item.autoCheck && (
              <span className="text-[10px] bg-teal/20 text-teal px-1.5 py-0.5 rounded border border-teal/30">auto</span>
            )}
            {item.hint && (
              <span className="text-[10px] text-txt-muted flex items-center gap-0.5"><Info className="w-3 h-3" />{item.hint}</span>
            )}
            {item.tooltip && (
              <span className="text-[10px] text-txt-muted cursor-help" title={item.tooltip}>
                <Info className="w-3 h-3 text-teal/60 hover:text-teal" />
              </span>
            )}
          </div>
          {/* Extra fields based on type */}
          {item.type === "checkbox_date" && val.checked && (
            <input type="date" value={val.date || ""} onChange={e => handleFieldChange(item.id, "date", e.target.value)} className="mt-2 px-2 py-1 text-xs border border-navy-border rounded bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-1 focus:ring-teal" disabled={readOnly} />
          )}
          {item.type === "checkbox_amount" && val.checked && (
            <div className="flex items-center gap-1 mt-2">
              <span className="text-xs text-txt-muted">£</span>
              <input type="number" value={val.amount || ""} onChange={e => handleFieldChange(item.id, "amount", e.target.value)} className="w-32 px-2 py-1 text-xs border border-navy-border rounded bg-[rgba(26,37,71,0.85)] text-white focus:outline-none focus:ring-1 focus:ring-teal" placeholder="Amount" disabled={readOnly} />
            </div>
          )}
          {item.type === "checkbox_notes" && val.checked && (
            <input type="text" value={val.notes || ""} onChange={e => handleFieldChange(item.id, "notes", e.target.value)} className="mt-2 w-full px-2 py-1 text-xs border border-navy-border rounded bg-[rgba(26,37,71,0.85)] text-white placeholder:text-txt-muted focus:outline-none focus:ring-1 focus:ring-teal" placeholder="Notes..." disabled={readOnly} />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="checklist-engine">
      {/* Overall Progress Bar */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-white">Overall Progress</span>
          <span className="text-sm font-bold text-teal">{progress.checked}/{progress.total} ({progress.pct}%)</span>
        </div>
        <div className="h-2 bg-navy-hover rounded-md overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal to-teal rounded-md transition-all duration-500"
            style={{ width: `${progress.pct}%` }}
          />
        </div>
      </div>

      {/* Sections */}
      {config.sections.map(section => {
        if (!isSectionVisible(section)) return null;
        const isCollapsed = collapsedSections[section.id];
        const sp = sectionProgress(section);
        const allDone = sp.total > 0 && sp.checked === sp.total;

        return (
          <div key={section.id} className="glass-card overflow-hidden" data-testid={`checklist-section-${section.id}`}>
            {/* Section Header */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-navy-hover/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isCollapsed ? <ChevronRight className="w-4 h-4 text-txt-muted" /> : <ChevronDown className="w-4 h-4 text-teal" />}
                {allDone ? (
                  <CheckCircle2 className="w-5 h-5 text-teal" />
                ) : (
                  <FileText className="w-5 h-5 text-txt-muted" />
                )}
                <span className="text-sm font-bold text-white">{section.title}</span>
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-md border ${
                allDone 
                  ? "bg-teal/15 text-teal border-teal/30" 
                  : "bg-navy-hover text-txt-muted border-navy-border"
              }`}>
                {sp.checked}/{sp.total}
              </span>
            </button>
            {section.description && !isCollapsed && (
              <p className="px-4 pb-2 text-xs text-txt-muted">{section.description}</p>
            )}
            {matterReference && !isCollapsed && (section.agentSkills || []).length > 0 && (
              <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid={`agent-skills-${section.id}`}>
                {section.agentSkills.map(task => (
                  <a
                    key={task}
                    href={agentSkillHref(matterReference, clientName, task)}
                    title={`Ask the conveyancing agent for a ${task.toLowerCase()} on ${matterReference}. It drafts; you check and sign off.`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border border-teal/30 bg-teal/10 text-teal hover:bg-teal/20"
                  >
                    <Bot className="w-3.5 h-3.5" />
                    {task}
                  </a>
                ))}
              </div>
            )}
            {/* Section Items */}
            {!isCollapsed && (
              <div className="border-t border-navy-border/40 px-2 pb-2 divide-y divide-navy-border/20">
                {section.items.map(item => renderItem(item))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
